/** Shared shape for every Recovery Trauma™ therapy explainer page. */

export type EvidenceTier = "established" | "broader" | "emerging";

export type TherapyGroup =
  | "trauma-processing"
  | "body-based"
  | "cognitive-behavioural"
  | "relational-depth"
  | "parts-compassion";

export type TherapyReference = {
  label: string;
  source: string;
  url: string;
};

export type TherapyFaq = {
  q: string;
  a: string;
};

export type TherapyLink = {
  label: string;
  to: string;
  blurb: string;
};

export type Therapy = {
  slug: string;
  name: string;
  abbrev?: string;
  /** Short category label shown on the card, e.g. "Trauma Processing". */
  badge: string;
  group: TherapyGroup;
  /** 1–2 sentence card explanation. */
  tagline: string;
  evidence: EvidenceTier;
  /** One honest line about how strong the evidence actually is. */
  evidenceSummary: string;
  whatIsIt: string[];
  howItWorks: string[];
  theory: string[];
  session: string[];
  techniques: string[];
  usedFor: string[];
  trauma: string[];
  complexTrauma: string[];
  duration: string[];
  experience: string[];
  benefits: string[];
  limitations: string[];
  evidenceBase: string[];
  whoProvides: string[];
  selfHelp: string[];
  questions: string[];
  /** Plain question-and-answer prose, always rendered for readers and crawlers. */
  faq?: TherapyFaq[];
  resources: TherapyLink[];
  references: TherapyReference[];
};

export const EVIDENCE_LABEL: Record<EvidenceTier, string> = {
  established: "Established trauma treatment",
  broader: "Broader psychotherapy approach",
  emerging: "Complementary / emerging evidence",
};

export const EVIDENCE_BLURB: Record<EvidenceTier, string> = {
  established:
    "Recommended for PTSD in major clinical guidelines, supported by multiple randomised controlled trials.",
  broader:
    "A well-established psychotherapy with a solid general evidence base, but less trauma-specific trial evidence than the front-line PTSD treatments.",
  emerging:
    "Promising and widely used, but the trauma-specific research base is smaller or of more mixed quality.",
};

export const GROUP_LABEL: Record<TherapyGroup, string> = {
  "trauma-processing": "Trauma processing therapies",
  "body-based": "Body-based & nervous system approaches",
  "cognitive-behavioural": "Cognitive & behavioural approaches",
  "relational-depth": "Relational & depth approaches",
  "parts-compassion": "Parts work & compassion-based approaches",
};

export const GROUP_BLURB: Record<TherapyGroup, string> = {
  "trauma-processing":
    "Structured therapies designed specifically to work with traumatic memory.",
  "body-based": "Approaches that begin with the body and the nervous system rather than the story.",
  "cognitive-behavioural":
    "Working with thoughts, beliefs, behaviour and the meaning you've made of what happened.",
  "relational-depth":
    "Understanding patterns, relationships and the past as it shows up in the present.",
  "parts-compassion":
    "Meeting the different parts of yourself, and building an inner tone that isn't harsh.",
};
